/* =========================================================
   ESBT SHOPIFY HORIZON
   GLOBAL HEADER + REUSABLE INTERACTION FEEDBACK
========================================================= */

(function () {
  'use strict';

  const BUILD_ID = '2026-09-07-shopify-interactions-v4';
  const WHATSAPP_NUMBER =
    typeof Theme !== 'undefined' && Theme.contact && Theme.contact.whatsapp_number
      ? Theme.contact.whatsapp_number
      : '966505455053';
  const WHATSAPP_PROMPT_SESSION_KEY = 'esbt-whatsapp-prompt-seen';
  const LANGUAGE_CHOICE_STORAGE_KEY = 'esbt-language-choice';
  const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  const CART_ERROR_EVENT = 'shopify:cart:error';

  const toastRecords = new Map();
  const buttonRecords = new WeakMap();
  const pendingCartFeedback = new WeakMap();
  let toastSequence = 0;
  let fallbackCartRefreshTimer = 0;

  document.documentElement.setAttribute('data-esbt-build', BUILD_ID);

  function isArabicPage() {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    const dir = (document.documentElement.getAttribute('dir') || '').toLowerCase();
    return lang.startsWith('ar') || dir === 'rtl';
  }

  function copy(english, arabic) {
    return isArabicPage() ? arabic : english;
  }

  function applyLanguageClasses() {
    if (!document.body) return;
    const arabic = isArabicPage();
    document.body.classList.toggle('esbt-show-ar', arabic);
    document.body.classList.toggle('esbt-show-en', !arabic);
  }

  function getCartUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) return Theme.routes.cart_url;
    return '/cart';
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    return false;
  }

  function getCartJsonUrl() {
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    return root + 'cart.js';
  }

  function shouldDisplayVatInclusivePrices() {
    return document.documentElement.dataset.esbtVatDisplay === 'true';
  }

  function priceWithVat(amount) {
    const numericAmount = Number(amount || 0);
    return shouldDisplayVatInclusivePrices() ? numericAmount * 1.15 : numericAmount;
  }

  function formatMoneyWithoutCurrency(amount) {
    return priceWithVat(amount).toLocaleString(isArabicPage() ? 'ar-SA' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function restartClassAnimation(element, className) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    window.setTimeout(function () {
      element.classList.remove(className);
    }, 700);
  }

  function cartValues(cart) {
    if (!cart) return {};

    let count;
    let total;

    if (cart.item_count !== undefined) count = Number(cart.item_count);
    else if (cart.totalQuantity !== undefined) count = Number(cart.totalQuantity);

    if (cart.total_price !== undefined) total = Number(cart.total_price) / 100;
    else if (cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount !== undefined) {
      total = Number(cart.cost.totalAmount.amount);
    }

    return {
      count: Number.isFinite(count) ? count : undefined,
      total: Number.isFinite(total) ? total : undefined
    };
  }

  function updateCartUI(cart) {
    const values = cartValues(cart);

    if (values.count !== undefined) {
      document.querySelectorAll('[data-esbt-cart-count]').forEach(function (node) {
        const nextValue = values.count > 99 ? '99+' : String(values.count);
        const changed = node.textContent.trim() !== nextValue;
        node.textContent = nextValue;
        node.classList.toggle('is-empty', values.count <= 0);
        if (changed) restartClassAnimation(node, 'is-updated');
      });
    }

    if (values.total !== undefined) {
      const nextTotal = formatMoneyWithoutCurrency(values.total);
      document.querySelectorAll('[data-esbt-cart-total]').forEach(function (node) {
        const changed = node.textContent.trim() !== nextTotal;
        node.textContent = nextTotal;
        if (changed) restartClassAnimation(node, 'is-updated');
      });
    }

    return values;
  }

  async function refreshCart(options) {
    const settings = options || {};
    const response = await fetch(getCartJsonUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Could not refresh cart');

    const cart = await response.json();
    updateCartUI(cart);
    if (settings.animate) animateCart();
    return cart;
  }

  function animateCart() {
    const targets = document.querySelectorAll(
      '.esbt-header-cart, cart-icon, .header-actions__cart-icon, [data-esbt-cart-link]'
    );

    targets.forEach(function (target) {
      restartClassAnimation(target, 'esbt-cart-confirmed');
    });
  }

  function ensureToastRegion() {
    let region = document.querySelector('[data-esbt-toast-region]');
    if (region) return region;

    region = document.createElement('div');
    region.className = 'esbt-toast-region';
    region.dataset.esbtToastRegion = '';
    region.setAttribute('aria-label', copy('Notifications', 'الإشعارات'));
    document.body.appendChild(region);
    return region;
  }

  function dismissToast(key) {
    const record = toastRecords.get(key);
    if (!record) return;

    if (record.timer) window.clearTimeout(record.timer);
    toastRecords.delete(key);
    record.element.classList.remove('is-visible');
    record.element.classList.add('is-leaving');

    window.setTimeout(function () {
      record.element.remove();
    }, 220);
  }

  function createToastAction(action, key) {
    const control = action.href ? document.createElement('a') : document.createElement('button');
    control.className = 'esbt-toast__action' + (action.primary ? ' is-primary' : '');
    control.textContent = action.label;

    if (action.href) {
      control.href = action.href;
    } else {
      control.type = 'button';
    }

    control.addEventListener('click', function (event) {
      if (typeof action.onClick === 'function') action.onClick(event);
      if (action.dismiss !== false) dismissToast(key);
    });

    return control;
  }

  function renderToast(record, options) {
    const type = options.type || 'info';
    const element = record.element;
    element.className = 'esbt-toast esbt-toast--' + type;
    element.dir = isArabicPage() ? 'rtl' : 'ltr';
    element.setAttribute('role', type === 'error' ? 'alert' : 'status');
    element.setAttribute('aria-atomic', 'true');
    element.replaceChildren();

    const icon = document.createElement('span');
    icon.className = 'esbt-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'success' ? '✓' : type === 'error' ? '!' : type === 'loading' ? '' : 'i';

    const content = document.createElement('div');
    content.className = 'esbt-toast__content';

    const title = document.createElement('strong');
    title.className = 'esbt-toast__title';
    title.textContent = options.title || '';
    content.appendChild(title);

    if (options.message) {
      const message = document.createElement('span');
      message.className = 'esbt-toast__message';
      message.textContent = options.message;
      content.appendChild(message);
    }

    if (options.actions && options.actions.length) {
      const actions = document.createElement('div');
      actions.className = 'esbt-toast__actions';
      options.actions.forEach(function (action) {
        actions.appendChild(createToastAction(action, record.key));
      });
      content.appendChild(actions);
    }

    const close = document.createElement('button');
    close.className = 'esbt-toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', copy('Dismiss notification', 'إغلاق الإشعار'));
    close.textContent = '×';
    close.addEventListener('click', function () {
      dismissToast(record.key);
    });

    element.append(icon, content, close);
  }

  function showToast(options) {
    const settings = options || {};
    const key = settings.key || 'esbt-toast-' + String(++toastSequence);
    let record = toastRecords.get(key);

    if (!record) {
      const element = document.createElement('aside');
      record = { key, element, timer: 0 };
      toastRecords.set(key, record);
      ensureToastRegion().appendChild(element);
    }

    if (record.timer) window.clearTimeout(record.timer);
    renderToast(record, settings);

    window.requestAnimationFrame(function () {
      record.element.classList.add('is-visible');
    });

    const duration = settings.duration === undefined
      ? settings.type === 'loading'
        ? 0
        : settings.actions && settings.actions.length
          ? 6500
          : 3800
      : settings.duration;

    if (duration > 0) {
      record.timer = window.setTimeout(function () {
        dismissToast(key);
      }, duration);
    }

    if (toastRecords.size > 3) {
      const oldestKey = toastRecords.keys().next().value;
      if (oldestKey !== key) dismissToast(oldestKey);
    }

    return {
      key,
      element: record.element,
      dismiss: function () {
        dismissToast(key);
      },
      update: function (nextOptions) {
        showToast(Object.assign({}, nextOptions, { key }));
      }
    };
  }

  function setButtonState(button, state, label) {
    if (!(button instanceof HTMLElement)) return;

    let record = buttonRecords.get(button);
    if (!record) {
      const status = document.createElement('span');
      status.className = 'esbt-action-status';
      status.setAttribute('aria-hidden', 'true');
      button.style.setProperty('--esbt-action-status-color', window.getComputedStyle(button).color);
      record = {
        status,
        wasDisabled: button instanceof HTMLButtonElement ? button.disabled : false,
        timer: 0
      };
      buttonRecords.set(button, record);
      button.appendChild(status);
    }

    if (record.timer) window.clearTimeout(record.timer);
    record.status.textContent = label || '';
    button.dataset.esbtState = state;
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    if (button instanceof HTMLButtonElement && !record.wasDisabled) button.disabled = true;
  }

  function clearButtonState(button) {
    if (!(button instanceof HTMLElement)) return;
    const record = buttonRecords.get(button);
    if (!record) return;

    if (record.timer) window.clearTimeout(record.timer);
    record.status.remove();
    button.removeAttribute('data-esbt-state');
    button.removeAttribute('aria-busy');
    button.style.removeProperty('--esbt-action-status-color');
    if (button instanceof HTMLButtonElement) button.disabled = record.wasDisabled;
    buttonRecords.delete(button);
  }

  function flashButtonState(button, state, label, duration) {
    setButtonState(button, state, label);
    const record = buttonRecords.get(button);
    if (!record) return;

    record.timer = window.setTimeout(function () {
      clearButtonState(button);
    }, duration || 1300);
  }

  function findActionButton(source) {
    if (!(source instanceof Element)) return null;
    if (source.matches('button, [role="button"]')) return source;

    return source.querySelector(
      '[ref="addToCartButton"], [data-esbt-add], .add-to-cart-button, button[name="add"], .quick-add__button'
    );
  }

  function cartContext(event) {
    const source = event.target instanceof Element ? event.target : null;
    const row = source && source.closest('[data-esbt-row]');
    const productFamily = source && source.closest('[data-esbt-pdp]');
    const line = event.lines && event.lines.length ? event.lines[0] : null;
    const quantityInput = row && row.querySelector('[data-esbt-qty]');
    const quantity = Number((line && line.quantity) || (quantityInput && quantityInput.value) || 1);

    let productTitle = (source && source.dataset.esbtProductTitle) ||
      (productFamily && productFamily.dataset.esbtProductTitle) || '';
    let variantTitle = (source && source.dataset.esbtVariantTitle) ||
      (row && row.dataset.esbtVariantTitle) || '';
    let sku = (source && source.dataset.esbtVariantSku) || (row && row.dataset.esbtVariantSku) || '';

    if (!productTitle && source) {
      const heading = source.closest('.shopify-section')?.querySelector('h1, .product-title');
      productTitle = heading ? heading.textContent.trim() : '';
    }

    return {
      source,
      button: findActionButton(source),
      productTitle,
      variantTitle: variantTitle === 'Default Title' ? '' : variantTitle,
      sku,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    };
  }

  function cartItemSummary(context) {
    const parts = [];
    if (context.productTitle) parts.push(context.quantity + ' × ' + context.productTitle);
    if (context.variantTitle) parts.push(context.variantTitle);
    if (context.sku) parts.push('SKU ' + context.sku);
    return parts.join(' · ');
  }

  function completeCartSuccess(feedback, payload) {
    if (feedback.settled) return;
    feedback.settled = true;

    const values = updateCartUI(payload && payload.cart);
    if (values.total === undefined || values.count === undefined) {
      refreshCart().catch(function () {
        /* Horizon still owns the authoritative cart UI. */
      });
    }

    animateCart();
    if (feedback.button) {
      setButtonState(feedback.button, 'success', copy('Added to Cart ✓', 'تمت الإضافة إلى السلة ✓'));
      window.setTimeout(function () {
        clearButtonState(feedback.button);
      }, 1600);
    }

    showToast({
      key: feedback.toastKey,
      type: 'success',
      title: copy('Added to cart', 'تمت الإضافة إلى السلة'),
      message: cartItemSummary(feedback.context),
      actions: [
        {
          label: copy('View Cart', 'عرض السلة'),
          onClick: function () {
            if (!openCartDrawer()) window.location.href = getCartUrl();
          },
          primary: true
        },
        {
          label: copy('Continue Shopping', 'متابعة التسوق')
        }
      ]
    });
  }

  function completeCartError(feedback, error) {
    if (feedback.settled) return;
    feedback.settled = true;

    if (feedback.button) {
      setButtonState(feedback.button, 'error', copy('Try again', 'حاول مرة أخرى'));
      window.setTimeout(function () {
        clearButtonState(feedback.button);
      }, 1900);
    }

    showToast({
      key: feedback.toastKey,
      type: 'error',
      title: copy('Could not add item', 'تعذرت إضافة المنتج'),
      message: error && error.message
        ? error.message
        : copy('Please check the quantity and try again.', 'تحقق من الكمية ثم حاول مرة أخرى.'),
      duration: 6500
    });
  }

  function handleCartLinesUpdate(event) {
    if (fallbackCartRefreshTimer) {
      window.clearTimeout(fallbackCartRefreshTimer);
      fallbackCartRefreshTimer = 0;
    }

    const isAdd = event.action === 'add';
    const context = cartContext(event);
    let feedback = null;

    if (isAdd) {
      const sourceKey = context.source || document.documentElement;
      const sourceId = context.source && context.source.id ? context.source.id : String(++toastSequence);
      feedback = {
        context,
        button: context.button,
        settled: false,
        toastKey: 'esbt-cart-add-' + sourceId
      };
      pendingCartFeedback.set(sourceKey, feedback);

      if (context.button) {
        setButtonState(context.button, 'loading', copy('Adding…', 'جارٍ الإضافة…'));
      }

      showToast({
        key: feedback.toastKey,
        type: 'loading',
        title: copy('Adding to cart…', 'جارٍ الإضافة إلى السلة…'),
        message: cartItemSummary(context),
        duration: 0
      });
    }

    const settle = function (payload) {
      const detail = payload && payload.detail;

      if (isAdd && feedback) {
        if (detail && detail.didError) completeCartError(feedback);
        else completeCartSuccess(feedback, payload);
      } else {
        const values = updateCartUI(payload && payload.cart);
        if (values.count === undefined || values.total === undefined) {
          refreshCart().catch(function () {});
        }
      }
    };

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(settle).catch(function (error) {
        if (isAdd && feedback) completeCartError(feedback, error);
      });
    } else {
      refreshCart().then(function (cart) {
        settle({ cart });
      }).catch(function (error) {
        if (isAdd && feedback) completeCartError(feedback, error);
      });
    }
  }

  function handleCartError(event) {
    const source = event.target instanceof Element ? event.target : document.documentElement;
    const feedback = pendingCartFeedback.get(source);
    const error = event.error instanceof Error
      ? event.error
      : new Error(event.error || (event.detail && event.detail.error) || '');

    if (feedback) {
      completeCartError(feedback, error);
      return;
    }

    showToast({
      key: 'esbt-cart-error',
      type: 'error',
      title: copy('Cart not updated', 'لم يتم تحديث السلة'),
      message: error.message || copy('Please try again.', 'يرجى المحاولة مرة أخرى.'),
      duration: 6000
    });
  }

  function setupCartFeedback() {
    if (document.documentElement.dataset.esbtCartFeedbackReady === 'true') return;
    document.documentElement.dataset.esbtCartFeedbackReady = 'true';

    refreshCart().catch(function () {
      /* Keep Liquid-rendered cart values when an initial refresh is unavailable. */
    });

    document.addEventListener(CART_LINES_UPDATE_EVENT, handleCartLinesUpdate);
    document.addEventListener(CART_ERROR_EVENT, handleCartError);

    ['cart:update', 'cart:refresh', 'product:added', 'cart:changed'].forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        window.setTimeout(function () {
          refreshCart().catch(function () {});
        }, 100);
      });
    });

    document.addEventListener('submit', function (event) {
      if (!(event.target instanceof Element)) return;
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      if (fallbackCartRefreshTimer) window.clearTimeout(fallbackCartRefreshTimer);
      fallbackCartRefreshTimer = window.setTimeout(function () {
        refreshCart({ animate: true }).catch(function () {});
      }, 1200);
    });

    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refreshCart().catch(function () {});
    });
  }

  function selectedProductContext(anchor) {
    const productFamily = anchor.closest('[data-esbt-pdp]');
    if (!productFamily) return null;

    const selectedId = productFamily.dataset.esbtSelectedVariantId;
    const activeRow = productFamily.querySelector('[data-esbt-row].is-active') ||
      (selectedId ? productFamily.querySelector('[data-esbt-row][data-variant-id="' + selectedId + '"]') : null);

    const productTitle = productFamily.dataset.esbtProductTitle || '';
    const variantTitle = activeRow && activeRow.dataset.esbtVariantTitle !== 'Default Title'
      ? activeRow.dataset.esbtVariantTitle
      : '';
    const sku = activeRow ? activeRow.dataset.esbtVariantSku || '' : '';
    const specs = activeRow ? activeRow.dataset.esbtVariantSpecs || '' : '';
    const productUrl = new URL(window.location.href);

    if (activeRow) productUrl.searchParams.set('variant', activeRow.dataset.variantId);

    return { productTitle, variantTitle, sku, specs, productUrl: productUrl.toString() };
  }

  function addProductContextToWhatsApp(anchor) {
    const context = selectedProductContext(anchor);
    if (!context) {
      try {
        const genericDestination = new URL(anchor.href);
        if (!genericDestination.searchParams.get('text')) {
          genericDestination.searchParams.set(
            'text',
            copy(
              'Hello ESBT, I would like a quotation for:',
              'مرحباً ESBT، أرغب بطلب عرض سعر لـ:'
            )
          );
          anchor.href = genericDestination.toString();
        }
      } catch (error) {
        /* Keep the original WhatsApp URL if it cannot be parsed. */
      }
      return;
    }

    const lines = isArabicPage()
      ? ['مرحباً ESBT، أحتاج مساعدة فنية بخصوص المنتج التالي:']
      : ['Hello ESBT, I need technical help with the following product:'];

    if (context.productTitle) lines.push(copy('Product: ', 'المنتج: ') + context.productTitle);
    if (context.variantTitle) lines.push(copy('Model: ', 'الموديل: ') + context.variantTitle);
    if (context.sku) lines.push('SKU: ' + context.sku);
    if (context.specs) lines.push(copy('Specifications: ', 'المواصفات: ') + context.specs);
    lines.push(copy('Product URL: ', 'رابط المنتج: ') + context.productUrl);

    try {
      const destination = new URL(anchor.href);
      destination.searchParams.set('text', lines.join('\n'));
      anchor.href = destination.toString();
    } catch (error) {
      /* Keep the original WhatsApp URL if it cannot be parsed. */
    }
  }

  function primeWhatsAppFeedback(anchor) {
    if (anchor.classList.contains('esbt-footer__quote')) {
      flashButtonState(anchor, 'loading', '', 1300);
      return;
    }

    if (anchor.classList.contains('esbt-whatsapp')) {
      anchor.classList.add('is-opening');
      anchor.setAttribute('aria-busy', 'true');
      window.setTimeout(function () {
        anchor.classList.remove('is-opening');
        anchor.removeAttribute('aria-busy');
      }, 1200);
      return;
    }

    if (anchor.closest('[data-esbt-home]')) {
      flashButtonState(anchor, 'loading', copy('Opening…', 'جارٍ الفتح…'), 1300);
      return;
    }

    if (anchor.matches('.esbt-header-quote, .esbt-btn, .esbt-pdp-help-btn')) {
      flashButtonState(anchor, 'loading', copy('Opening WhatsApp…', 'جارٍ فتح واتساب…'), 1300);
      return;
    }

    anchor.classList.add('esbt-link-opening');
    anchor.setAttribute('aria-busy', 'true');
    window.setTimeout(function () {
      anchor.classList.remove('esbt-link-opening');
      anchor.removeAttribute('aria-busy');
    }, 1200);
  }

  function openWhatsAppWithFeedback(anchor) {
    addProductContextToWhatsApp(anchor);
    primeWhatsAppFeedback(anchor);
    showToast({
      key: 'esbt-whatsapp-opening',
      type: 'info',
      title: copy('Opening WhatsApp…', 'جارٍ فتح واتساب…'),
      message: copy('Your message is ready with the relevant details.', 'تم تجهيز الرسالة بالتفاصيل ذات الصلة.'),
      duration: 2600
    });
  }

  function setupExternalActionFeedback() {
    if (document.documentElement.dataset.esbtExternalFeedbackReady === 'true') return;
    document.documentElement.dataset.esbtExternalFeedbackReady = 'true';

    document.addEventListener('pointerdown', function (event) {
      if (!(event.target instanceof Element)) return;
      const whatsapp = event.target.closest('a[href*="wa.me"]');
      if (whatsapp) primeWhatsAppFeedback(whatsapp);
    });

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href]');
      if (!link) return;

      if (link.matches('a[href*="wa.me"]')) {
        openWhatsAppWithFeedback(link);
        return;
      }

      if (link.classList.contains('esbt-datasheet-btn')) {
        flashButtonState(link, 'success', copy('Datasheet opened ✓', 'تم فتح ورقة البيانات ✓'), 1400);
        showToast({
          key: 'esbt-datasheet-opened',
          type: 'success',
          title: copy('Datasheet opened', 'تم فتح ورقة البيانات'),
          message: copy('The technical PDF opened in a new tab.', 'تم فتح ملف PDF الفني في علامة تبويب جديدة.'),
          duration: 3200
        });
      }
    });
  }

  function ensureFloatingWhatsapp() {
    if (!document.body || document.querySelector('.esbt-whatsapp')) return;

    const message = copy(
      'Hello ESBT, I would like a quotation for:',
      'مرحباً ESBT، أرغب بطلب عرض سعر لـ:'
    );

    const button = document.createElement('a');
    button.className = 'esbt-whatsapp';
    button.href = 'https://wa.me/' + WHATSAPP_NUMBER + '?text=' + encodeURIComponent(message);
    button.target = '_blank';
    button.rel = 'noopener';
    button.dataset.esbtWhatsapp = '';
    button.setAttribute('aria-label', copy('Contact ESBT on WhatsApp', 'تواصل مع ESBT عبر واتساب'));
    button.innerHTML = '<img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="" width="34" height="34">';
    document.body.appendChild(button);
  }

  function rememberWhatsAppPrompt() {
    try {
      window.sessionStorage.setItem(whatsAppPromptStorageKey(), 'true');
    } catch (error) {
      /* The prompt still works when browser storage is unavailable. */
    }
  }

  function wasWhatsAppPromptSeen() {
    try {
      return window.sessionStorage.getItem(whatsAppPromptStorageKey()) === 'true';
    } catch (error) {
      return false;
    }
  }

  function currentLanguageCode() {
    return (document.documentElement.getAttribute('lang') || 'en').toLowerCase().split('-')[0];
  }

  function whatsAppPromptStorageKey() {
    return WHATSAPP_PROMPT_SESSION_KEY + ':' + currentLanguageCode();
  }

  function storedLanguageChoice() {
    try {
      return window.localStorage.getItem(LANGUAGE_CHOICE_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function rememberLanguageChoice(languageCode) {
    try {
      window.localStorage.setItem(LANGUAGE_CHOICE_STORAGE_KEY, languageCode);
    } catch (error) {
      /* Shopify still applies the selected locale when browser storage is unavailable. */
    }
  }

  function setupLanguageLinks() {
    document.querySelectorAll('[data-esbt-language-link]').forEach(function (link) {
      if (link.dataset.esbtLanguageLinkReady === 'true') return;
      link.dataset.esbtLanguageLinkReady = 'true';

      link.addEventListener('click', function () {
        const targetUrl = new URL(link.dataset.esbtLanguageUrl || link.href, window.location.origin);
        targetUrl.search = window.location.search;
        targetUrl.hash = window.location.hash;
        link.href = targetUrl.pathname + targetUrl.search + targetUrl.hash;
      });
    });
  }

  function setupLanguageGate() {
    const gate = document.querySelector('[data-esbt-language-gate]');
    if (!gate || gate.dataset.esbtReady === 'true') return false;

    gate.dataset.esbtReady = 'true';
    if (storedLanguageChoice()) return false;

    document.body.classList.add('esbt-language-gate-open');
    gate.hidden = false;
    gate.setAttribute('aria-hidden', 'false');

    Array.from(document.body.children).forEach(function (element) {
      if (element !== gate) element.inert = true;
    });

    gate.querySelectorAll('[data-esbt-language-choice]').forEach(function (choice) {
      choice.addEventListener('click', function () {
        const languageCode = choice.dataset.esbtLanguageChoice || '';
        if (languageCode) rememberLanguageChoice(languageCode);

        gate.classList.add('is-selecting');
        gate.setAttribute('aria-busy', 'true');
        choice.setAttribute('aria-busy', 'true');
      });
    });

    gate.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;

      const choices = Array.from(gate.querySelectorAll('[data-esbt-language-choice]'));
      if (!choices.length) return;

      const firstChoice = choices[0];
      const lastChoice = choices[choices.length - 1];

      if (event.shiftKey && document.activeElement === firstChoice) {
        event.preventDefault();
        lastChoice.focus();
      } else if (!event.shiftKey && document.activeElement === lastChoice) {
        event.preventDefault();
        firstChoice.focus();
      }
    });

    window.requestAnimationFrame(function () {
      gate.classList.add('is-visible');
      const firstChoice = gate.querySelector('[data-esbt-language-choice]');
      if (firstChoice) firstChoice.focus({ preventScroll: true });
    });

    return true;
  }

  function setupHomepageWhatsAppPrompt() {
    if (document.body.classList.contains('esbt-language-gate-open')) return;

    const prompt = document.querySelector('[data-esbt-whatsapp-prompt]');
    if (!prompt || prompt.dataset.esbtReady === 'true') return;

    prompt.dataset.esbtReady = 'true';
    const close = prompt.querySelector('[data-esbt-whatsapp-prompt-close]');
    const action = prompt.querySelector('[data-esbt-whatsapp-prompt-action]');
    const isThemeEditor = Boolean(window.Shopify && Shopify.designMode);

    if (!isThemeEditor && wasWhatsAppPromptSeen()) return;

    let hideTimer = 0;

    function hidePrompt(remember) {
      if (remember) rememberWhatsAppPrompt();
      if (hideTimer) window.clearTimeout(hideTimer);
      prompt.classList.remove('is-visible');
      prompt.setAttribute('aria-hidden', 'true');
      hideTimer = window.setTimeout(function () {
        prompt.hidden = true;
      }, 220);
    }

    if (close) {
      close.addEventListener('click', function () {
        hidePrompt(true);
      });
    }

    if (action) {
      action.addEventListener('click', function () {
        hidePrompt(true);
      });
    }

    prompt.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        hidePrompt(true);
      }
    });

    window.setTimeout(function () {
      prompt.hidden = false;
      prompt.setAttribute('aria-hidden', 'false');
      if (!isThemeEditor) rememberWhatsAppPrompt();
      window.requestAnimationFrame(function () {
        prompt.classList.add('is-visible');
      });
    }, isThemeEditor ? 300 : 4500);
  }

  function setupHomepageDiscovery() {
    document.querySelectorAll('[data-esbt-home]').forEach(function (home) {
      if (home.dataset.discoveryReady === 'true') return;
      home.dataset.discoveryReady = 'true';
      const searchForm = home.querySelector('[data-esbt-home-search]');
      const searchInput = searchForm?.querySelector('input[name="q"]');

      if (searchForm && searchInput) {
        const mobilePlaceholder = searchInput.dataset.mobilePlaceholder;
        const desktopPlaceholder = searchInput.placeholder;
        const mobileQuery = window.matchMedia('(max-width: 749px)');
        const syncPlaceholder = function () {
          searchInput.placeholder = mobileQuery.matches && mobilePlaceholder
            ? mobilePlaceholder
            : desktopPlaceholder;
        };

        syncPlaceholder();
        if (typeof mobileQuery.addEventListener === 'function') {
          mobileQuery.addEventListener('change', syncPlaceholder);
        }

        searchForm.addEventListener('submit', function () {
          const button = searchForm.querySelector('button[type="submit"]');
          setButtonState(button, 'loading', copy('Loading…', 'تحميل…'));
        });
      }

      home.addEventListener('click', function (event) {
        if (!(event.target instanceof Element) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

        const buttonLink = event.target.closest('a.esbt-home__button');
        if (buttonLink && !event.defaultPrevented) {
          const isNewTab = buttonLink.target === '_blank' || buttonLink.hasAttribute('download');
          if (isNewTab) {
            flashButtonState(buttonLink, 'loading', copy('Opening…', 'جارٍ الفتح…'), 1600);
          } else {
            setButtonState(buttonLink, 'loading', copy('Loading…', 'تحميل…'));
          }
          return;
        }

        const focusLink = event.target.closest('[data-esbt-home-search-focus]');
        if (focusLink && searchInput) {
          event.preventDefault();
          searchInput.scrollIntoView({ block: 'center', behavior: 'instant' });
          searchInput.focus({ preventScroll: true });
          return;
        }

        const liveSearch = event.target.closest('[data-esbt-home-search-open]');
        if (!liveSearch) return;
        const dialog = document.getElementById('search-modal');
        // Keep the ordinary search link if Horizon's dialog module is unavailable.
        if (!dialog || typeof dialog.showDialog !== 'function') return;
        event.preventDefault();
        dialog.setAttribute('dir', isArabicPage() ? 'rtl' : 'ltr');
        dialog.showDialog();
        window.requestAnimationFrame(function () {
          const nativeInput = dialog.querySelector('input[name="q"]');
          if (!nativeInput) return;
          nativeInput.value = searchInput.value;
          nativeInput.focus();
          if (nativeInput.value.trim()) {
            nativeInput.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
          }
        });
      });
    });
  }

  function setupFooterReveal() {
    const revealItems = document.querySelectorAll('.esbt-footer [data-esbt-footer-reveal]');
    if (!revealItems.length) return;

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach(function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    document.querySelectorAll('.esbt-footer').forEach(function (footer) {
      if (footer.dataset.footerRevealReady === 'true') return;
      footer.dataset.footerRevealReady = 'true';
      footer.classList.add('esbt-footer--reveal-ready');

      const observer = new IntersectionObserver(function (entries, currentObserver) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
        });
      }, {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.12
      });

      footer.querySelectorAll('[data-esbt-footer-reveal]').forEach(function (item) {
        observer.observe(item);
      });
    });
  }

  window.ESBTInteractions = {
    showToast,
    dismissToast,
    setButtonState,
    clearButtonState,
    flashButtonState,
    animateCart,
    updateCartUI,
    refreshCart,
    openWhatsAppWithFeedback
  };

  function boot() {
    applyLanguageClasses();
    setupCartFeedback();
    setupExternalActionFeedback();
    ensureFloatingWhatsapp();
    setupLanguageLinks();
    setupLanguageGate();
    setupHomepageDiscovery();
    setupHomepageWhatsAppPrompt();
    setupFooterReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    applyLanguageClasses();
    setupLanguageLinks();
    setupHomepageDiscovery();
    setupHomepageWhatsAppPrompt();
    setupFooterReveal();
  });
})();
